import NoteAdd from '@mui/icons-material/NoteAdd';
import { Box, CircularProgress, Divider, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Dominios from '@/util/Dominios';
import ReactComponent from '../../../../ReactComponent';
import ModalConfirmacao from '../../../ModalConfirmacao';

class PainelPosicionamentos extends ReactComponent {
    posicionamentos = [];
    carregando = false;
    abrirConfirmacaoAdicionarRascunho = false;
    textoConfirmacao = null;
    siglaConfirmacao = null;
    tipoConfirmacao = null;

    constructor(props) {
        super(props);

        makeObservable(this, {
            posicionamentos: observable,
            carregando: observable,
            abrirConfirmacaoAdicionarRascunho: observable,
            textoConfirmacao: observable,
            siglaConfirmacao: observable,
            tipoConfirmacao: observable,
            carregarPosicionamentos: action,
            handleClickAdicionarRascunho: action,
            fecharConfirmacao: action
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    _carregamentoIniciado = false;

    componentDidMount() {
        if (!this._carregamentoIniciado) {
            this._carregamentoIniciado = true;
            this.carregarPosicionamentos();
        }
    }

    adicionarPosicionamento(tipo, sigla, conteudo) {
        if (!this.posicionamentos.some((p) => p.tipo === tipo && p.sigla === sigla && p.conteudo === conteudo)) {
            this.posicionamentos.push({ tipo, sigla, conteudo });
        }
    }

    carregarPosicionamentos = async () => {
        if (this.carregando) return;
        this.carregando = true;
        this.posicionamentos = [];

        try {
            await Promise.all([
                this.carregarRessalvas(),
                this.carregarDivergencias(),
                this.carregarDestaques(),
                this.carregarRetornoVista()
            ]);
        } finally {
            runInAction(() => {
                this.carregando = false;
            });
        }
    };

    carregarRessalvas = async () => {
        if (!this.registro.votos) return;

        const siglasConfirmadas = [
            ...new Set(
                this.registro.votos
                    .filter(
                        (v) =>
                            v.voto?.codigo === Dominios.CODIGO_VOTO_RESSALVA && v.situacaoVoto === 'CONFIRMADO'
                    )
                    .map((v) => v.siglaDoMagistrado)
            )
        ];

        if (siglasConfirmadas.length === 0) return;

        const promises = siglasConfirmadas.map((sigla) =>
            this.processoService.recuperarObservacaoVoto(
                this.registro.sessaoId,
                this.registro.processo.numeroCompleto,
                Dominios.TIPO_OBSERVACAO_VOTO_RESSALVA,
                sigla
            )
        );
        const respostas = await Promise.all(promises);

        const promisesConteudo = [];
        respostas.forEach((resposta) => {
            resposta.forEach((observacaoVoto) => {
                promisesConteudo.push(
                    this.processoService.recuperarObservacaoVotoPeloId(
                        this.registro.sessaoId,
                        this.registro.processo.numeroCompleto,
                        Dominios.TIPO_OBSERVACAO_VOTO_RESSALVA,
                        observacaoVoto.magistrado.sigla,
                        observacaoVoto.id
                    )
                );
            });
        });

        const respostasConteudo = await Promise.all(promisesConteudo);
        respostasConteudo.forEach((resposta) => {
            runInAction(() => this.adicionarPosicionamento('Ressalva', resposta.magistrado.sigla, resposta.conteudo));
        });
    };

    carregarDivergencias = async () => {
        if (!this.registro.votos) return;

        const siglasConfirmadas = [
            ...new Set(
                this.registro.votos
                    .filter(
                        (v) =>
                            v.voto?.codigo === Dominios.CODIGO_VOTO_DIVERGENTE && v.situacaoVoto === 'CONFIRMADO'
                    )
                    .map((v) => v.siglaDoMagistrado)
            )
        ];

        if (siglasConfirmadas.length === 0) return;

        const promises = siglasConfirmadas.map((sigla) =>
            this.processoService.recuperarObservacaoVoto(
                this.registro.sessaoId,
                this.registro.processo.numeroCompleto,
                Dominios.TIPO_OBSERVACAO_VOTO_DIVERGENCIA,
                sigla
            )
        );
        const respostas = await Promise.all(promises);

        const observacoesVoto = [];
        respostas.forEach((resposta) => {
            resposta.forEach((obs) => {
                if (typeof obs === 'object') {
                    observacoesVoto.push(obs);
                }
            });
        });

        for (const observacaoVoto of observacoesVoto) {
            const sigla = observacaoVoto.magistrado.sigla;
            let conteudo = observacaoVoto.conteudo;

            if (!observacaoVoto.tipoConteudo) {
                conteudo = (
                    await this.processoService.recuperarObservacaoVotoPeloId(
                        this.registro.sessaoId,
                        this.registro.processo.numeroCompleto,
                        Dominios.TIPO_OBSERVACAO_VOTO_DIVERGENCIA,
                        sigla,
                        observacaoVoto.id
                    )
                ).conteudo;
            } else if (observacaoVoto.tipoConteudo === 'HTML') {
                conteudo = await this.processoService.recuperarConteudoObservacaoVotoPeloId(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    Dominios.TIPO_OBSERVACAO_VOTO_DIVERGENCIA,
                    sigla,
                    observacaoVoto.id
                );
            }

            runInAction(() => this.adicionarPosicionamento('Itens de divergência', sigla, conteudo));
        }
    };

    carregarDestaques = async () => {
        if (!this.registro.contabilizacoesObservacao) return;

        const destaquesPublicos = this.registro.contabilizacoesObservacao.filter(
            (o) => o.tipoObservacao === 'DESTAQUE' && o.visibilidade === 'PUBLICA'
        );

        if (destaquesPublicos.length === 0) return;

        for (const observacao of destaquesPublicos) {
            const destaques = await this.sessaoService.recuperarDestaques(
                this.registro.sessaoId,
                this.registro.numeroProcesso,
                observacao.siglaMagistrado
            );

            for (const destaque of destaques) {
                if (destaque.tipoConteudo === 'RTF') continue;

                const conteudoDestaque = await this.sessaoService.recuperarConteudoDestaque(
                    this.registro.sessaoId,
                    this.registro.numeroProcesso,
                    destaque.magistrado.sigla,
                    destaque.id
                );

                runInAction(() => this.adicionarPosicionamento('Manifestação', destaque.magistrado.sigla, conteudoDestaque.conteudo));
            }
        }
    };

    carregarRetornoVista = async () => {
        const vistas = this.registro.informacoesAdicionais?.RetornoVistaRegimental;
        if (!vistas || vistas.length === 0) return;

        for (const vista of vistas) {
            const sigla = vista.magistrado?.sigla || vista.magistrado;

            try {
                const votoVista = await this.processoService.recuperarObservacaoVoto(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    Dominios.TIPO_OBSERVACAO_VOTO_MINUTA_RETORNO_VISTA,
                    sigla
                );

                if (votoVista && votoVista[0]) {
                    const conteudo = await this.processoService.recuperarConteudoObservacaoVotoPeloId(
                        this.registro.sessaoId,
                        this.registro.processo.numeroCompleto,
                        Dominios.TIPO_OBSERVACAO_VOTO_MINUTA_RETORNO_VISTA,
                        sigla,
                        votoVista[0].id
                    );

                    runInAction(() => this.adicionarPosicionamento('Minuta de retorno de vista', sigla, conteudo.trim()));
                }
            } catch {
                // Ignora erros individuais de carregamento
            }
        }
    };

    limparHtml(html) {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    handleClickAdicionarRascunho = async (tipo, sigla, texto) => {
        texto = this.limparHtml(texto);
        let rascunho = await this.certidaoService.recuperarRascunhoCertidao(this.registro);

        if (rascunho.indexOf(texto) === -1 || this.abrirConfirmacaoAdicionarRascunho) {
            if (tipo === 'Ressalva') {
                const magistrado = this.dominioStore.magistradosComSigla.find((mag) => mag.sigla === sigla);
                const tratamento = this.salaSessaoStore.pronomeTratamentoComCargo(magistrado);
                rascunho = this.salaSessaoStore.renumerarObservacoes(rascunho);
                rascunho = `${rascunho}\n\nObservação ${this.salaSessaoStore.getQuantidadeObservacoes(rascunho) + 1}: ${tratamento} ${magistrado.nome} registrou ressalva de entendimento pessoal.\n${texto}\n`;
            } else {
                rascunho += '\n\n' + texto;
            }

            await this.certidaoService.salvarRascunhoCertidao(this.registro, rascunho);
            runInAction(() => (this.registro.rascunhoCertidao = rascunho));
            this.mensagemStore.abrirMensagemSucesso(`Texto adicionado ao rascunho da certidão`);
            this.abrirConfirmacaoAdicionarRascunho = false;
            this.textoConfirmacao = null;
            this.siglaConfirmacao = null;
            this.tipoConfirmacao = null;
        } else {
            this.abrirConfirmacaoAdicionarRascunho = true;
            this.textoConfirmacao = texto;
            this.siglaConfirmacao = sigla;
            this.tipoConfirmacao = tipo;
        }
    };

    fecharConfirmacao = () => {
        this.abrirConfirmacaoAdicionarRascunho = false;
        this.textoConfirmacao = null;
        this.siglaConfirmacao = null;
        this.tipoConfirmacao = null;
    };

    agruparPorTipo() {
        const grupos = {};
        this.posicionamentos.forEach((p) => {
            if (!grupos[p.tipo]) {
                grupos[p.tipo] = [];
            }
            grupos[p.tipo].push(p);
        });
        return grupos;
    }

    render() {
        if (this.carregando) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={30} disableShrink={true} />
                </Box>
            );
        }

        if (this.posicionamentos.length === 0) {
            return (
                <Box sx={{ py: 2 }}>
                    <Typography color="text.secondary" align="center">
                        Não há posicionamentos públicos
                    </Typography>
                </Box>
            );
        }

        const grupos = this.agruparPorTipo();

        return (
            <Box>
                {Object.entries(grupos).map(([tipo, itens], indexGrupo) => (
                    <Box key={this.nextKey} sx={{ mb: 2 }}>
                        {indexGrupo > 0 && <Divider sx={{ mb: 2 }} />}
                        <Typography fontWeight="bold" sx={{ mb: 1 }}>
                            {tipo}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {itens.map((item) => (
                                <Paper
                                    key={this.nextKey}
                                    elevation={3}
                                    sx={{ display: 'flex', flexDirection: 'column', m: 0.5, flex: '1 1 300px', maxWidth: '640px' }}
                                >
                                    <Box sx={{ m: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {item.sigla}
                                            </Typography>
                                            <Tooltip title="Adicionar ao rascunho da certidão" placement="top">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        this.handleClickAdicionarRascunho(
                                                            item.tipo,
                                                            item.sigla,
                                                            item.conteudo
                                                        )
                                                    }
                                                >
                                                    <NoteAdd style={{ color: 'black' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Box
                                            dangerouslySetInnerHTML={{ __html: item.conteudo }}
                                            sx={{
                                                overflowX: 'auto',
                                                minHeight: '40px',
                                                maxHeight: '200px',
                                                textAlign: 'justify',
                                                overflowY: 'auto'
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                ))}
                <ModalConfirmacao
                    titulo="Adicionar texto ao rascunho da certidão"
                    open={this.abrirConfirmacaoAdicionarRascunho}
                    callbackCancelar={this.fecharConfirmacao}
                    callbackConfirmar={() =>
                        this.handleClickAdicionarRascunho(
                            this.tipoConfirmacao,
                            this.siglaConfirmacao,
                            this.textoConfirmacao
                        )
                    }
                >
                    <Typography display="inline">
                        O texto foi encontrado no rascunho da certidão. Deseja adicionar novamente?
                    </Typography>
                </ModalConfirmacao>
            </Box>
        );
    }
}

export default inject('stores')(observer(PainelPosicionamentos));
